import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  DollarSign, ShoppingCart, TrendingUp, TrendingDown, Package, 
  Pill, Activity, Calendar, Filter, RefreshCw,
  ChevronUp, ChevronDown, Search, AlertTriangle, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Date preset helpers
const getDatePreset = (preset) => {
  const today = new Date();
  const formatDate = (d) => d.toISOString().split('T')[0];
  
  switch (preset) {
    case 'today':
      return { from: formatDate(today), to: formatDate(today) };
    case 'yesterday':
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { from: formatDate(yesterday), to: formatDate(yesterday) };
    case 'last7days':
      const week = new Date(today);
      week.setDate(week.getDate() - 6);
      return { from: formatDate(week), to: formatDate(today) };
    case 'thisMonth':
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: formatDate(monthStart), to: formatDate(today) };
    case 'lastMonth':
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return { from: formatDate(lastMonthStart), to: formatDate(lastMonthEnd) };
    case 'thisQuarter':
      const quarter = Math.floor(today.getMonth() / 3);
      const quarterStart = new Date(today.getFullYear(), quarter * 3, 1);
      return { from: formatDate(quarterStart), to: formatDate(today) };
    case 'thisYear':
      const yearStart = new Date(today.getFullYear(), 0, 1);
      return { from: formatDate(yearStart), to: formatDate(today) };
    default:
      return { from: formatDate(new Date(today.setDate(today.getDate() - 30))), to: formatDate(new Date()) };
  }
};

// Chart colors
const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

// KPI Card Component
const KPICard = ({ title, value, formatted, change, direction, icon: Icon, subtitle, loading }) => {
  if (loading) {
    return (
      <Card className="bg-card/50 backdrop-blur">
        <CardContent className="p-4">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-8 bg-muted rounded w-3/4"></div>
            <div className="h-3 bg-muted rounded w-1/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="bg-card/50 backdrop-blur border-border/50 hover:border-primary/30 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold">{formatted || value?.toLocaleString()}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <div className={`p-2 rounded-lg ${direction === 'up' ? 'bg-green-500/10' : direction === 'down' ? 'bg-red-500/10' : 'bg-muted'}`}>
            <Icon className={`w-5 h-5 ${direction === 'up' ? 'text-green-500' : direction === 'down' ? 'text-red-500' : 'text-muted-foreground'}`} />
          </div>
        </div>
        {change !== undefined && (
          <div className={`flex items-center gap-1 mt-2 text-xs ${direction === 'up' ? 'text-green-500' : direction === 'down' ? 'text-red-500' : 'text-muted-foreground'}`}>
            {direction === 'up' ? <ChevronUp className="w-3 h-3" /> : direction === 'down' ? <ChevronDown className="w-3 h-3" /> : null}
            <span>{change}% vs previous period</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Empty State Component
const EmptyState = ({ message }) => (
  <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
    <Activity className="w-12 h-12 mb-2 opacity-30" />
    <p className="text-sm">{message || 'No data available for this period'}</p>
  </div>
);

// Loading Skeleton for Charts
const ChartSkeleton = () => (
  <div className="animate-pulse space-y-4 p-4">
    <div className="h-4 bg-muted rounded w-1/4"></div>
    <div className="h-[250px] bg-muted/50 rounded"></div>
  </div>
);

export default function SalesAnalyticsPage() {
  const { currentOrg, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Check super admin access
  useEffect(() => {
    if (user && user.role !== 'super_admin') {
      toast.error('Access denied. Super admin only.');
      navigate('/');
    }
  }, [user, navigate]);
  
  // Filter state from URL params
  const [filters, setFilters] = useState(() => {
    const preset = searchParams.get('preset') || 'last7days';
    const dates = getDatePreset(preset);
    return {
      fromDate: searchParams.get('from') || dates.from,
      toDate: searchParams.get('to') || dates.to,
      preset: preset,
      category: searchParams.get('category') || '',
      paymentMethod: searchParams.get('payment') || '',
      itemType: searchParams.get('type') || '',
      itemId: searchParams.get('item') || '',
      supplierId: searchParams.get('supplier') || ''
    };
  });
  
  // Data state
  const [kpis, setKpis] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState(null);
  const [topProducts, setTopProducts] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [profitTrend, setProfitTrend] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [slowMoving, setSlowMoving] = useState(null);
  const [nearExpiry, setNearExpiry] = useState(null);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  
  // Search state for dropdowns
  const [itemSearch, setItemSearch] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [granularity, setGranularity] = useState('daily');
  const [topProductsSort, setTopProductsSort] = useState('revenue');
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionSearch, setTransactionSearch] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  
  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.fromDate) params.set('from', filters.fromDate);
    if (filters.toDate) params.set('to', filters.toDate);
    if (filters.preset) params.set('preset', filters.preset);
    if (filters.category) params.set('category', filters.category);
    if (filters.paymentMethod) params.set('payment', filters.paymentMethod);
    if (filters.itemType) params.set('type', filters.itemType);
    if (filters.itemId) params.set('item', filters.itemId);
    if (filters.supplierId) params.set('supplier', filters.supplierId);
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);
  
  // Fetch categories, suppliers, and inventory items for filters
  useEffect(() => {
    if (!currentOrg) return;
    
    // Fetch categories
    axios.get(`${API}/sales-analytics/categories?organization_id=${currentOrg.id}`)
      .then(res => setCategories(res.data.categories || []))
      .catch(() => {});
    
    // Fetch suppliers
    axios.get(`${API}/suppliers?organization_id=${currentOrg.id}`)
      .then(res => setSuppliers(res.data || []))
      .catch(() => {});
    
    // Fetch inventory items - get all items for search (use larger page size)
    axios.get(`${API}/inventory?organization_id=${currentOrg.id}&page_size=5000`)
      .then(res => setInventoryItems(res.data.items || []))
      .catch(() => {});
  }, [currentOrg]);
  
  // Fetch all data
  const fetchData = useCallback(async () => {
    if (!currentOrg) {
      console.log('No currentOrg - skipping fetch');
      return;
    }
    
    console.log('Fetching analytics for org:', currentOrg.id);
    setLoading(true);
    const params = new URLSearchParams({
      organization_id: currentOrg.id,
      from_date: filters.fromDate,
      to_date: filters.toDate
    });
    if (filters.category) params.set('category', filters.category);
    if (filters.paymentMethod) params.set('payment_method', filters.paymentMethod);
    if (filters.itemType) params.set('item_type', filters.itemType);
    if (filters.itemId) params.set('item_id', filters.itemId);
    if (filters.supplierId) params.set('supplier_id', filters.supplierId);
    
    try {
      const [kpiRes, trendRes, topRes, catRes, payRes, heatRes, profitRes] = await Promise.all([
        axios.get(`${API}/sales-analytics/kpis?${params}`).catch(e => { console.error('KPI error:', e); return { data: null }; }),
        axios.get(`${API}/sales-analytics/revenue-trend?${params}&granularity=${granularity}`).catch(e => { console.error('Trend error:', e); return { data: null }; }),
        axios.get(`${API}/sales-analytics/top-products?${params}&sort_by=${topProductsSort}`).catch(e => { console.error('Top products error:', e); return { data: null }; }),
        axios.get(`${API}/sales-analytics/sales-by-category?${params}`).catch(e => { console.error('Category error:', e); return { data: null }; }),
        axios.get(`${API}/sales-analytics/payment-methods?${params}`).catch(e => { console.error('Payment error:', e); return { data: null }; }),
        axios.get(`${API}/sales-analytics/hourly-heatmap?${params}`).catch(e => { console.error('Heatmap error:', e); return { data: null }; }),
        axios.get(`${API}/sales-analytics/profit-trend?organization_id=${currentOrg.id}`).catch(e => { console.error('Profit error:', e); return { data: null }; })
      ]);
      
      console.log('KPI response:', kpiRes.data);
      console.log('Trend response:', trendRes.data);
      
      setKpis(kpiRes.data);
      setRevenueTrend(trendRes.data);
      setTopProducts(topRes.data);
      setCategoryData(catRes.data);
      setPaymentData(payRes.data);
      setHeatmapData(heatRes.data);
      setProfitTrend(profitRes.data);
      
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [currentOrg, filters, granularity, topProductsSort]);
  
  // Fetch transactions
  const fetchTransactions = useCallback(async () => {
    if (!currentOrg) return;
    
    const params = new URLSearchParams({
      organization_id: currentOrg.id,
      from_date: filters.fromDate,
      to_date: filters.toDate,
      page: transactionPage,
      page_size: 20
    });
    if (transactionSearch) params.set('search', transactionSearch);
    if (filters.paymentMethod) params.set('payment_method', filters.paymentMethod);
    
    try {
      const res = await axios.get(`${API}/sales-analytics/transactions?${params}`);
      setTransactions(res.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  }, [currentOrg, filters, transactionPage, transactionSearch]);
  
  // Fetch slow moving and near expiry
  const fetchInventoryAlerts = useCallback(async () => {
    if (!currentOrg) return;
    
    try {
      const [slowRes, expiryRes] = await Promise.all([
        axios.get(`${API}/sales-analytics/slow-moving?organization_id=${currentOrg.id}&from_date=${filters.fromDate}&to_date=${filters.toDate}`).catch(() => ({ data: null })),
        axios.get(`${API}/sales-analytics/near-expiry?organization_id=${currentOrg.id}`).catch(() => ({ data: null }))
      ]);
      
      setSlowMoving(slowRes.data);
      setNearExpiry(expiryRes.data);
    } catch (error) {
      console.error('Error fetching inventory alerts:', error);
    }
  }, [currentOrg, filters]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  useEffect(() => {
    if (activeTab === 'transactions') fetchTransactions();
  }, [activeTab, fetchTransactions]);
  
  useEffect(() => {
    if (activeTab === 'inventory') fetchInventoryAlerts();
  }, [activeTab, fetchInventoryAlerts]);
  
  // Date preset handler
  const handlePreset = (preset) => {
    const dates = getDatePreset(preset);
    setFilters(f => ({ ...f, fromDate: dates.from, toDate: dates.to, preset }));
  };
  
  if (!currentOrg) return <div className="p-8 text-center text-muted-foreground">Loading...</div>;
  
  return (
    <div className="min-h-screen bg-background p-4 md:p-6 space-y-6" data-testid="sales-analytics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="w-6 h-6 text-primary" />
            Sales Analytics & Reports
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Comprehensive insights into your seafood sales performance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Filters */}
      <Card className="bg-card/50 backdrop-blur">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            {/* Date Presets */}
            <div className="space-y-1.5">
              <Label className="text-xs">Quick Select</Label>
              <div className="flex flex-wrap gap-1">
                {[
                  { key: 'today', label: 'Today' },
                  { key: 'yesterday', label: 'Yesterday' },
                  { key: 'last7days', label: 'Last 7 Days' },
                  { key: 'thisMonth', label: 'This Month' },
                  { key: 'lastMonth', label: 'Last Month' },
                  { key: 'thisQuarter', label: 'This Quarter' },
                  { key: 'thisYear', label: 'This Year' }
                ].map(p => (
                  <Button
                    key={p.key}
                    variant={filters.preset === p.key ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => handlePreset(p.key)}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Custom Date Range */}
            <div className="space-y-1.5">
              <Label className="text-xs">From</Label>
              <Input
                type="date"
                value={filters.fromDate}
                onChange={(e) => setFilters(f => ({ ...f, fromDate: e.target.value, preset: 'custom' }))}
                className="h-8 w-36"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">To</Label>
              <Input
                type="date"
                value={filters.toDate}
                onChange={(e) => setFilters(f => ({ ...f, toDate: e.target.value, preset: 'custom' }))}
                className="h-8 w-36"
              />
            </div>
            
            {/* Category Filter - Searchable */}
            <div className="space-y-1.5 relative">
              <Label className="text-xs">Category</Label>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search category..."
                  value={categorySearch || filters.category}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  onFocus={() => { if (filters.category) setCategorySearch(filters.category); }}
                  className="h-8 w-36 pr-8"
                />
                {filters.category && (
                  <button 
                    onClick={() => { setFilters(f => ({ ...f, category: '' })); setCategorySearch(''); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    ×
                  </button>
                )}
              </div>
              {categorySearch && !filters.category && (
                <div className="absolute z-50 top-full left-0 mt-1 w-48 max-h-48 overflow-auto bg-background border border-input rounded-md shadow-lg">
                  <button
                    onClick={() => { setFilters(f => ({ ...f, category: '' })); setCategorySearch(''); }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-muted text-muted-foreground"
                  >
                    All Categories
                  </button>
                  {categories
                    .filter(c => c && c.toLowerCase().includes(categorySearch.toLowerCase()))
                    .slice(0, 15)
                    .map(cat => (
                      <button
                        key={cat}
                        onClick={() => { setFilters(f => ({ ...f, category: cat })); setCategorySearch(''); }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-muted truncate"
                      >
                        {cat}
                      </button>
                    ))
                  }
                </div>
              )}
            </div>
            
            {/* Payment Method */}
            <div className="space-y-1.5">
              <Label className="text-xs">Payment</Label>
              <select
                value={filters.paymentMethod}
                onChange={(e) => setFilters(f => ({ ...f, paymentMethod: e.target.value }))}
                className="h-8 px-2 text-sm bg-background border border-input rounded-md"
              >
                <option value="">All Methods</option>
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="customer">Customer Account</option>
              </select>
            </div>
            
            {/* Item Search */}
            <div className="space-y-1.5 relative">
              <Label className="text-xs">Item</Label>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search item..."
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  className="h-8 w-40 pr-8"
                />
                {filters.itemId && (
                  <button 
                    onClick={() => { setFilters(f => ({ ...f, itemId: '' })); setItemSearch(''); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    ×
                  </button>
                )}
              </div>
              {itemSearch && !filters.itemId && (
                <div className="absolute z-50 top-full left-0 mt-1 w-64 max-h-48 overflow-auto bg-background border border-input rounded-md shadow-lg">
                  {inventoryItems
                    .filter(i => i.name?.toLowerCase().includes(itemSearch.toLowerCase()))
                    .slice(0, 10)
                    .map(item => (
                      <button
                        key={item.id}
                        onClick={() => { setFilters(f => ({ ...f, itemId: item.id })); setItemSearch(item.name); }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-muted truncate"
                      >
                        {item.name}
                      </button>
                    ))
                  }
                  {inventoryItems.filter(i => i.name?.toLowerCase().includes(itemSearch.toLowerCase())).length === 0 && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">No items found</div>
                  )}
                </div>
              )}
            </div>
            
            {/* Supplier Search */}
            <div className="space-y-1.5 relative">
              <Label className="text-xs">Supplier</Label>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search supplier..."
                  value={supplierSearch}
                  onChange={(e) => setSupplierSearch(e.target.value)}
                  className="h-8 w-40 pr-8"
                />
                {filters.supplierId && (
                  <button 
                    onClick={() => { setFilters(f => ({ ...f, supplierId: '' })); setSupplierSearch(''); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    ×
                  </button>
                )}
              </div>
              {supplierSearch && !filters.supplierId && (
                <div className="absolute z-50 top-full left-0 mt-1 w-64 max-h-48 overflow-auto bg-background border border-input rounded-md shadow-lg">
                  {suppliers
                    .filter(s => s.name?.toLowerCase().includes(supplierSearch.toLowerCase()))
                    .slice(0, 10)
                    .map(supplier => (
                      <button
                        key={supplier.id}
                        onClick={() => { setFilters(f => ({ ...f, supplierId: supplier.id })); setSupplierSearch(supplier.name); }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-muted truncate"
                      >
                        {supplier.name}
                      </button>
                    ))
                  }
                  {suppliers.filter(s => s.name?.toLowerCase().includes(supplierSearch.toLowerCase())).length === 0 && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">No suppliers found</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Navigation Tabs */}
      <div className="flex gap-1 border-b border-border">
        {['overview', 'transactions', 'inventory'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
      
      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <KPICard
              title="Total Revenue"
              value={kpis?.total_revenue?.value}
              formatted={kpis?.total_revenue?.formatted}
              change={kpis?.total_revenue?.change}
              direction={kpis?.total_revenue?.direction}
              icon={DollarSign}
              loading={loading}
            />
            <KPICard
              title="Transactions"
              value={kpis?.total_transactions?.value}
              change={kpis?.total_transactions?.change}
              direction={kpis?.total_transactions?.direction}
              icon={ShoppingCart}
              loading={loading}
            />
            <KPICard
              title="Avg Order Value"
              value={kpis?.average_order_value?.value}
              formatted={kpis?.average_order_value?.formatted}
              change={kpis?.average_order_value?.change}
              direction={kpis?.average_order_value?.direction}
              icon={TrendingUp}
              loading={loading}
            />
            <KPICard
              title="Gross Profit"
              value={kpis?.gross_profit?.value}
              formatted={kpis?.gross_profit?.formatted}
              change={kpis?.gross_profit?.change}
              direction={kpis?.gross_profit?.direction}
              icon={Activity}
              subtitle={`${kpis?.gross_profit?.margin || 0}% margin`}
              loading={loading}
            />
            <KPICard
              title="Units Sold"
              value={kpis?.units_sold?.value}
              change={kpis?.units_sold?.change}
              direction={kpis?.units_sold?.direction}
              icon={Package}
              loading={loading}
            />
            <KPICard
              title="Rx vs OTC"
              value={`${kpis?.prescription_otc_split?.prescription_pct || 0}%`}
              icon={Pill}
              subtitle={`Rx: ${kpis?.prescription_otc_split?.prescription_pct || 0}% | OTC: ${kpis?.prescription_otc_split?.otc_pct || 0}%`}
              loading={loading}
            />
          </div>
          
          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Trend */}
            <Card className="bg-card/50 backdrop-blur">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Revenue Trend</CardTitle>
                  <div className="flex gap-1">
                    {['daily', 'weekly', 'monthly'].map(g => (
                      <Button
                        key={g}
                        variant={granularity === g ? 'default' : 'ghost'}
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => setGranularity(g)}
                      >
                        {g.charAt(0).toUpperCase() + g.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? <ChartSkeleton /> : !revenueTrend?.current?.length ? <EmptyState /> : (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={revenueTrend.current}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="period" tick={{ fontSize: 10 }} className="text-muted-foreground" />
                      <YAxis tick={{ fontSize: 10 }} className="text-muted-foreground" />
                      <Tooltip 
                        contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                        formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']}
                      />
                      <Area type="monotone" dataKey="revenue" stroke="#10b981" fillOpacity={1} fill="url(#colorRevenue)" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
            
            {/* Top Products */}
            <Card className="bg-card/50 backdrop-blur">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Top 10 Products</CardTitle>
                  <div className="flex gap-1">
                    {['revenue', 'quantity'].map(s => (
                      <Button
                        key={s}
                        variant={topProductsSort === s ? 'default' : 'ghost'}
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => setTopProductsSort(s)}
                      >
                        By {s.charAt(0).toUpperCase() + s.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? <ChartSkeleton /> : !topProducts?.products?.length ? <EmptyState /> : (
                  <ResponsiveContainer width="100%" height={380}>
                    <BarChart data={topProducts.products} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 9 }} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                        formatter={(value, name) => [topProductsSort === 'revenue' ? `$${value.toLocaleString()}` : value, name]}
                      />
                      <Bar dataKey={topProductsSort} fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sales by Category - Horizontal Bar Chart for many categories */}
            <Card className="bg-card/50 backdrop-blur">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Sales by Category (Top 15)</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? <ChartSkeleton /> : !categoryData?.categories?.length ? <EmptyState /> : (
                  <ResponsiveContainer width="100%" height={Math.max(300, categoryData.categories.slice(0, 15).length * 28)}>
                    <BarChart data={categoryData.categories.slice(0, 15).map(c => ({ ...c, absRevenue: Math.abs(c.revenue) }))} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <YAxis dataKey="category" type="category" width={120} tick={{ fontSize: 9 }} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                        formatter={(value) => [`$${Math.abs(value).toLocaleString()}`, 'Revenue']}
                      />
                      <Bar dataKey="absRevenue" fill="#8b5cf6" radius={[0, 4, 4, 0]}>
                        {categoryData.categories.slice(0, 15).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
            
            {/* Payment Methods */}
            <Card className="bg-card/50 backdrop-blur">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Payment Methods</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? <ChartSkeleton /> : !paymentData?.methods?.length ? <EmptyState /> : (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={paymentData.methods.map(m => ({ ...m, absRevenue: Math.abs(m.revenue) }))}
                        dataKey="absRevenue"
                        nameKey="method"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        label={({ method, percentage }) => `${method}: ${percentage}%`}
                        labelLine={false}
                      >
                        {paymentData.methods.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                        formatter={(value) => [`$${Math.abs(value).toLocaleString()}`, 'Revenue']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
            
            {/* Profit Trend */}
            <Card className="bg-card/50 backdrop-blur">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Monthly Profit (12 months)</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? <ChartSkeleton /> : !profitTrend?.months?.length ? <EmptyState /> : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={profitTrend.months}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                        formatter={(value) => [`$${value.toLocaleString()}`, 'Profit']}
                      />
                      <Bar dataKey="profit" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Heatmap */}
          <Card className="bg-card/50 backdrop-blur">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Sales Heatmap (Hour × Day)
              </CardTitle>
              <CardDescription>Peak selling hours visualization</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? <ChartSkeleton /> : !heatmapData?.heatmap?.length ? <EmptyState /> : (
                <div className="overflow-x-auto">
                  <div className="min-w-[700px]">
                    <div className="flex">
                      <div className="w-12"></div>
                      {Array.from({ length: 24 }, (_, i) => (
                        <div key={i} className="flex-1 text-center text-[10px] text-muted-foreground">{i}</div>
                      ))}
                    </div>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, dayIdx) => (
                      <div key={day} className="flex items-center">
                        <div className="w-12 text-xs text-muted-foreground">{day}</div>
                        {Array.from({ length: 24 }, (_, hour) => {
                          const cell = heatmapData.heatmap.find(h => h.dayIndex === dayIdx && h.hour === hour);
                          const intensity = cell?.revenue || 0;
                          const maxRevenue = Math.max(...heatmapData.heatmap.map(h => h.revenue));
                          const opacity = maxRevenue > 0 ? Math.max(0.1, intensity / maxRevenue) : 0.1;
                          return (
                            <div
                              key={hour}
                              className="flex-1 h-6 m-0.5 rounded-sm cursor-pointer hover:ring-1 hover:ring-primary"
                              style={{ backgroundColor: `rgba(16, 185, 129, ${opacity})` }}
                              title={`${day} ${hour}:00 - $${intensity.toLocaleString()}`}
                            />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
      
      {activeTab === 'transactions' && (
        <Card className="bg-card/50 backdrop-blur">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Sales Transactions</CardTitle>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2 text-muted-foreground" />
                <Input
                  placeholder="Search receipt or customer..."
                  value={transactionSearch}
                  onChange={(e) => setTransactionSearch(e.target.value)}
                  className="pl-8 h-8 w-64"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="data-table text-sm w-full">
                <thead>
                  <tr>
                    <th>Receipt #</th>
                    <th>Date/Time</th>
                    <th>Items</th>
                    <th className="text-right">Total</th>
                    <th>Payment</th>
                    <th>Cashier</th>
                    <th>Customer</th>
                  </tr>
                </thead>
                <tbody>
                  {!transactions?.transactions?.length ? (
                    <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No transactions found</td></tr>
                  ) : transactions.transactions.map(t => (
                    <tr key={t.id} className="hover:bg-muted/50 cursor-pointer">
                      <td className="font-mono text-xs">{t.receipt_number}</td>
                      <td className="text-xs">{t.date} {t.time}</td>
                      <td>{t.items_count}</td>
                      <td className="text-right font-medium">${t.total?.toLocaleString()}</td>
                      <td className="capitalize">{t.payment_method}</td>
                      <td className="text-xs text-cyan-400">{t.cashier || '-'}</td>
                      <td className="text-xs">{t.customer_name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {transactions?.total_pages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Page {transactions.page} of {transactions.total_pages} ({transactions.total} total)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={transactionPage <= 1}
                    onClick={() => setTransactionPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={transactionPage >= transactions.total_pages}
                    onClick={() => setTransactionPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {activeTab === 'inventory' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Slow Moving Products */}
          <Card className="bg-card/50 backdrop-blur">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-yellow-500" />
                Slow-Moving Products
              </CardTitle>
              <CardDescription>Products with lowest sales velocity</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-[400px]">
                <table className="data-table text-sm w-full">
                  <thead className="sticky top-0 bg-card">
                    <tr>
                      <th>Product</th>
                      <th className="text-right">Stock</th>
                      <th className="text-right">Sold</th>
                      <th className="text-right">Velocity/Day</th>
                      <th className="text-right">Days of Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!slowMoving?.products?.length ? (
                      <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No data</td></tr>
                    ) : slowMoving.products.map(p => (
                      <tr key={p.item_id}>
                        <td className="text-xs">{p.name}</td>
                        <td className="text-right">{p.qty_on_hand}</td>
                        <td className="text-right">{p.qty_sold}</td>
                        <td className="text-right">{p.velocity_per_day}</td>
                        <td className="text-right">{p.days_of_stock > 999 ? '999+' : p.days_of_stock}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          
          {/* Near Expiry */}
          <Card className="bg-card/50 backdrop-blur">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Near-Expiry Stock
              </CardTitle>
              <CardDescription>
                Products expiring within 90 days • Total at risk: <span className="text-red-500 font-medium">${nearExpiry?.total_value_at_risk?.toLocaleString() || 0}</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-[400px]">
                <table className="data-table text-sm w-full">
                  <thead className="sticky top-0 bg-card">
                    <tr>
                      <th>Product</th>
                      <th className="text-right">Stock</th>
                      <th>Expiry</th>
                      <th className="text-right">Days Left</th>
                      <th className="text-right">Value at Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!nearExpiry?.products?.length ? (
                      <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No near-expiry products</td></tr>
                    ) : nearExpiry.products.map(p => (
                      <tr key={p.item_id} className={p.days_until_expiry < 30 ? 'bg-red-500/10' : ''}>
                        <td className="text-xs">{p.name}</td>
                        <td className="text-right">{p.qty_on_hand}</td>
                        <td className="text-xs">{p.expiry_date}</td>
                        <td className="text-right">{p.days_until_expiry}</td>
                        <td className="text-right text-red-500">${p.value_at_risk?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
